import { PaginationFilterType, Uuid, Location } from '@/api/types';
import { LocationFormType } from '@/types';
import { OpoRoleEnum } from '@/api/apis/customer';

export enum OpoUserInvitationState {
  InvitationInitiated = 'InvitationInitiated',
  InviteSent = 'InviteSent',
  Activated = 'Activated',
  Failed = 'Failed',
}

export enum OpoOrganizationOrderByOption {
  Name = 'Name',
  Code = 'Code',
  Country = 'Country',
  State = 'State',
  City = 'City',
  Address = 'Address',
  Timezone = 'Timezone',
  CreatedAt = 'CreatedAt',
  ModifiedAt = 'ModifiedAt',
}

export enum OpoContactOrderByOption {
  FirstName = 'FirstName',
  LastName = 'LastName',
  Email = 'Email',
  InvitationState = 'InvitationState',
  CreatedAt = 'CreatedAt',
  ModifiedAt = 'ModifiedAt',
}

export enum OpoContactTypeFilter {
  All = 'All',
  Contact = 'Contacts',
  User = 'Users',
}

export enum OpoPrimaryContactFilter {
  All = 'All',
  Primary = 'Primary',
  Secondary = 'Secondary',
}

export enum OpoUserInvitationFlowType {
  CreateContactAndInviteUser = 'CreateContactAndInviteUser',
  UpdateContactAndInviteUser = 'UpdateContactAndInviteUser',
}

type BaseOpoContact = {
  id: Uuid;
  userId: Uuid | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  primaryPhone: string;
  secondaryPhone: string | null;
  photoPath?: string;
  isActive: boolean;
  createdAt: string;
  modifiedAt: string;
  deletedAt: string | null;
};

export type BaseOpoOrganization = {
  id: Uuid;
  name: string;
  code: string;
  location: Location;
  createdAt: string;
  modifiedAt: string;
  deletedAt: string | null;
};

// OPO Organizations
export type OpoOrganizationContact = BaseOpoContact & {
  isPrimary: boolean;
  roles: OpoRoleEnum[];
};

export type OpoOrganization = BaseOpoOrganization & {
  contacts?: OpoOrganizationContact[];
};

// OPO Contacts
export type OpoContactOrganization = BaseOpoOrganization & {
  isPrimary: boolean;
  roles: OpoRoleEnum[];
};

export type OpoContact = BaseOpoContact & {
  isOpoUser: boolean;
  invitationState: OpoUserInvitationState;
  inviteSent: boolean;
  invitedAt: string | null;
  enabled: boolean;
  isLocked: boolean;
  lastInvitationError: string | null;
  organizations?: OpoContactOrganization[];
};

export type OpoOrganizationListFilters = {
  organizationIds?: string[];
  countries?: string[];
  states?: string[];
};

export type PaginatedOpoOrganizationListQueryParams = PaginationFilterType<
  OpoOrganizationListFilters,
  OpoOrganizationOrderByOption
>;

export type OpoContactListFilters = {
  primaryFilter: OpoPrimaryContactFilter;
  contactType: OpoContactTypeFilter;
  organizationIds?: string[];
  contactIds?: string[];
  priorityContactIds?: string[];
};

export type PaginatedOpoContactListQueryParams = PaginationFilterType<OpoContactListFilters, OpoContactOrderByOption>;

export type CreateOpoOrganizationRequestBody = {
  name: string;
  code: string;
  location: LocationFormType;
};

export type OpoOrganizationLocationBody = Pick<
  LocationFormType,
  'country' | 'state' | 'city' | 'addressLine' | 'postalCode' | 'timezone'
>;

export type PatchOpoOrganizationRequestBody = {
  name?: string | null;
  code?: string | null;
  location?: OpoOrganizationLocationBody;
  primaryContactId?: string | null;
};

export type OpoContactMembership = {
  opoOrganizationId: Uuid;
  roles: OpoRoleEnum[];
};

type BaseOpoContactFormType = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  primaryPhone: string | null;
  secondaryPhone?: string | null;
  photoPath?: string | null;
  isActive?: boolean;
  memberships: OpoContactMembership[];
  isAcceptedToReceiveNotifications?: boolean;
  isAcceptedTermsOfServiceAndPrivacyPolicy?: boolean;
};

export type CreateOpoContactRequestBody = BaseOpoContactFormType & {
  id?: Uuid;
  userId?: string | null;
};

export type CreateOpoUserRequestBody = CreateOpoContactRequestBody & {
  firstName: string;
  lastName: string;
  email: string;
};

export type UpdateOpoContactRequestBody = Partial<BaseOpoContactFormType>;

export type UpdateOpoUserRequestBody = UpdateOpoContactRequestBody;

export type PatchOwnOpoContactRequestBody = Partial<Omit<BaseOpoContactFormType, 'isActive' | 'memberships'>>;

export type OpoUserInvitationOperationResponse = {
  correlationId: string;
  flowType: OpoUserInvitationFlowType;
  status: string | null;
  contactId?: string | null;
  userId?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
};
